import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayerHeadshot from "../PlayerHeadshot";
import { getLastName } from "../../utils/textUtils";
import { formatBattingSlash } from "../../utils/fanPage";
import { colors } from "../../theme";
import PlayerNameLink from "./PlayerNameLink";

const BattingOrderRow = ({
  hole,
  player,
  editable = false,
  isFirst = false,
  isLast = false,
  compact = false,
  onSelect,
  onClear,
  onMove,
}) => {
  const filled = Boolean(player?.playerId);
  const clickable = editable || filled;
  const cleanup = hole.id === "4";
  const slash = filled ? formatBattingSlash(player) : "";

  return (
    <Box
      onClick={clickable ? () => onSelect?.(hole) : undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "40px 56px minmax(0, 1fr) auto",
          sm: "52px 64px minmax(0, 1fr) auto",
        },
        alignItems: "center",
        gap: { xs: 1.25, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        py: compact ? 0.75 : 1.25,
        cursor: clickable ? "pointer" : "default",
        borderBottom: `1px solid ${colors.border}`,
        bgcolor: cleanup ? "rgba(245, 158, 11, 0.06)" : "transparent",
        boxShadow: cleanup ? "inset 3px 0 0 #f59e0b" : "none",
        transition: "background-color 0.15s ease",
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": {
          bgcolor: editable
            ? cleanup
              ? "rgba(245, 158, 11, 0.1)"
              : "rgba(255,255,255,0.04)"
            : undefined,
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: compact ? { xs: 18, sm: 22 } : { xs: 22, sm: 26 },
            lineHeight: 1,
            letterSpacing: "-0.04em",
            color: cleanup ? "#f59e0b" : "text.primary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {hole.short}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: cleanup ? "#f59e0b" : "text.secondary",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
            fontSize: 10,
          }}
        >
          {hole.label}
        </Typography>
      </Box>

      <Box
        sx={{
          width: { xs: 52, sm: 60 },
          height: { xs: 52, sm: 60 },
          borderRadius: "50%",
          border: filled
            ? `2px solid ${cleanup ? "#f59e0b" : colors.positive}`
            : `2px dashed ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: filled ? "rgba(34, 197, 94, 0.08)" : "rgba(255,255,255,0.03)",
        }}
      >
        {filled ? (
          <PlayerHeadshot
            playerId={player.playerId}
            src={player.photoUrl}
            size={48}
            showTooltip={false}
            showLoading={false}
          />
        ) : (
          <AddIcon sx={{ color: editable ? "text.secondary" : "transparent" }} />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {filled ? (
          <PlayerNameLink
            playerId={player.playerId}
            name={getLastName(player.name)}
            linkable={player.linkable !== false}
          />
        ) : (
          <Typography variant="body1" fontWeight={700} noWrap>
            {editable ? `Pick your ${hole.pickLabel}` : "Open"}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap>
          {slash ||
            (filled
              ? player.team
              : compact
              ? "\u00a0"
              : editable
              ? "Anyone in the database"
              : "\u00a0")}
        </Typography>
      </Box>

      {editable && (
        <Box
          sx={{ display: "flex", alignItems: "center" }}
          onClick={(event) => event.stopPropagation()}
        >
          <IconButton
            size="small"
            aria-label={`Move ${hole.label} up`}
            disabled={isFirst || !filled}
            onClick={() => onMove?.(hole, -1)}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Move ${hole.label} down`}
            disabled={isLast || !filled}
            onClick={() => onMove?.(hole, 1)}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
          {filled && (
            <IconButton
              size="small"
              aria-label={`Clear ${hole.label}`}
              onClick={() => onClear?.(hole)}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BattingOrderRow;
